import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#end region
//#region types
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { CurrencyEnums } from "models/enums";
import { AgreementFormTypes } from "models/formTypes";
import { AgreementsListViewProps } from "models/propTypes/agreementPropTypes";
import { Chip } from "@mui/material";
//#endregion
//#region components
import { TableAsync, useFilterContext } from "components/common";
import { useAgreementService } from "serviceHooks";
import useCreateAgreementDialog from ".././agreement/forms/useCreateDialog";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const AgreementsList: React.FC<AgreementsListViewProps> = (props) => {
  const agreementService = useAgreementService();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { reloadKey, reloadList } = props;
  const { filter } = useFilterContext<AgreementFormTypes.Filter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => agreementService.reportFilter(baseFilter, filter),
    [filter]
  );

  const openEditAgreementDialog = useCreateAgreementDialog();

  const onRowClick = (row: WintSuperkollAPIModel.Agreement, rowIndex: number) => {
    dispatch(displayDialog(openEditAgreementDialog(row.id, reloadList, row.isNonEditable)));
  };

  return (
    <TableAsync<WintSuperkollAPIModel.Agreement>
      properties={{
        name: {
          description: TranslationKeys.Common.Name,
          className: cssExports.textBlack,
        },
        start: {
          description: TranslationKeys.Agreement.Begins,
          formatter: (agreement: WintSuperkollAPIModel.Agreement) => new Date(agreement.start),
          classification: "date",
        },
        end: {
          description: TranslationKeys.Agreement.Ends,
          formatter: (agreement: WintSuperkollAPIModel.Agreement) => (agreement.end ? new Date(agreement.end) : ""),
          classification: "date",
        },
        noticeDate: {
          description: TranslationKeys.Agreement.EndsLast,
          formatter: (agreement: WintSuperkollAPIModel.Agreement) =>
            agreement.noticeDate ? new Date(agreement.noticeDate) : "",
        },
        counterPart: {
          description: TranslationKeys.Agreement.Counterpart,
          formatter: (agreement: WintSuperkollAPIModel.Agreement) => agreement.counterPart,
        },
        active: {
          description: TranslationKeys.Common.Status,
          formatter: (agreement: WintSuperkollAPIModel.Agreement) => (
            <Chip
              label={LocalizationHelperInstance.getFormattedMessage(
                agreement.active ? TranslationKeys.Common.Active : TranslationKeys.Common.InActive
              )}
              className={agreement.active ? cssExports.greenTag : cssExports.redTag}
            />
          ),
          alignment: "center",
        },
        monthlyCost: {
          description: TranslationKeys.Agreement.MonthlyCost,
          formatter: (agreement: WintSuperkollAPIModel.Agreement) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, agreement.monthlyCost),
          className: cssExports.textBlack,
          alignment: "right",
        },
        totalCost: {
          description: TranslationKeys.Agreement.TotalCost,
          formatter: (agreement: WintSuperkollAPIModel.Agreement) =>
            new Amount(CurrencyEnums.CurrencyCode.SEK, agreement.totalCost),
          className: cssExports.textBlack,
          alignment: "right",
        },
      }}
      filter={filterFunc}
      rowClickHandler={onRowClick}
      pagination="advanced"
      reloadKey={reloadKey}
      maxRowCount={10}
      orderByProperty="start"
      orderByDescending
    />
  );
};

export default AgreementsList;
